import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Skeleton,
    Text,
    useBoolean,
    VStack,
} from '@chakra-ui/react';
import { format, parse } from 'date-fns';
import { Controller, useFormContext } from 'react-hook-form';
import { FaIcon, SkeletonMotion } from '@acp/core-ui';
import {
    OasBookingSlot, OasBookingTime,
} from '@acp/shared';
import { faArrowsRotate } from '@fortawesome/sharp-light-svg-icons';
import { useAvailableOasTimes } from '../../../booking/hooks/use-booking';
import { SubmitBookingFormSchemaValues } from './form-scheme';

function BookingTimesSkeleton() {
    return (
        <VStack alignItems="start" opacity={0.4} w="full" gap={4}>
            <SkeletonMotion height={8} width="50%" sx={{ animationDuration: '2s' }} />
            <Skeleton height={28} width="100%" sx={{ animationDuration: '2.4s' }} />
        </VStack>
    );
}

function NoBookingTimeSlots() {
    return (
        <Flex
            flexDir="column"
            alignItems="center"
            justifyContent="center"
            py={8}
            px={8}
            borderColor="muted.50"
            borderWidth={1}
            rounded="md"
        >
            <Text fontSize="sm" color="black">No booking times available. Please try again later or contact us</Text>
        </Flex>
    );
}

export function AvailableBookingTimes(
    {
        propertyId,
        onChooseTimeSlot,
        handleResetTime,
    }: {
        propertyId: number;
        onChooseTimeSlot: () => void;
        handleResetTime: () => void;
    }
) {
    const [_isSpinning, setIsSpinning] = useBoolean();
    const {
        control, setValue, getValues, watch,
    } = useFormContext<SubmitBookingFormSchemaValues>();

    const oasResultData = getValues('oasBooking.oasResult');
    const bookingTypes = watch('oasBooking.bookingTypes');

    const {
        data: oasBookingTimes, isLoading, refetch,
    } = useAvailableOasTimes({
        propertyId,
        service: bookingTypes,
    });

    if (isLoading) {
        return <BookingTimesSkeleton />;
    }

    if (!isLoading && (!oasBookingTimes || oasBookingTimes?.length === 0)) {
        return <NoBookingTimeSlots />;
    }

    const handleSelectTime = (oasTime: OasBookingTime, booking: OasBookingSlot) => {
        setValue('oasBooking.oasResult', {
            ...oasResultData,
            oasMetadataId: booking.oasMetadataId,
            date: oasTime.date,
            startTime: booking.timeFrom,
            endTime: booking.timeTo,
            technicianId: booking.technicianId,
        });

        onChooseTimeSlot();
    };

    const handleRefeshBookingTime = () => {
        setIsSpinning.on();
        void refetch();
        handleResetTime();
        setTimeout(() => {
            setIsSpinning.off();
        }, 1000);
    };

    return (
        <Controller
            control={control}
            name="oasBooking.oasResult"
            render={({ fieldState }) => (
                <FormControl
                    isInvalid={fieldState.invalid}
                    isRequired
                    mb={2}
                >
                    <FormLabel>
                        Available booking times
                        <Button onClick={handleRefeshBookingTime} variant="unstyled">
                            <FaIcon
                                icon={faArrowsRotate}
                                fontSize="sm"
                                color="muted"
                                _hover={{ opacity: 0.5 }}
                                className={_isSpinning ? 'fa-spin' : ''}
                            />
                        </Button>
                    </FormLabel>

                    <Accordion defaultIndex={[0]} allowMultiple borderColor="transparent" w="full">
                        {oasBookingTimes?.map((oasTime) => (
                            <AccordionItem key={oasTime.date}>
                                <AccordionButton _hover={{ bg: 'transparent' }}>
                                    <Box w="full" textAlign="left" fontWeight="500">
                                        {format(new Date(oasTime.date), 'EEEE, do MMMM yyyy')}
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel pb={4}>
                                    <HStack wrap="wrap">
                                        {oasTime.bookingTimes.map((booking) => (
                                            <Button
                                                w="fit-content"
                                                variant="solid"
                                                colorScheme="secondary"
                                                size="sm"
                                                key={booking.timeFrom}
                                                _hover={{ opacity: 0.8 }}
                                                onClick={() => handleSelectTime(oasTime, booking)}
                                            >
                                                {format(parse(booking.timeFrom, 'HH:mm', new Date()), 'h:mm aaa')}
                                                {' - '}
                                                {format(parse(booking.timeTo, 'HH:mm', new Date()), 'h:mm aaa').toLowerCase()}
                                            </Button>
                                        ))}
                                    </HStack>
                                </AccordionPanel>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </FormControl>
            )}
        />
    );
}
